import { Injectable, inject } from '@angular/core';
import { Route, Router, UrlSegment, UrlTree } from '@angular/router';
import { guestsRoute } from '@auth/auth.routes';
import { AuthService } from '@core/auth/auth.service';
import { OfficeTenantsService } from '@office/tenants/office-tenants.service';
import { Permission } from '@shared/enums/permission.enum';
import { AuthorizedRouteBasedOnPermission, AuthorizedRouteBasedOnRole, NotAuthorizedExactRouteBasedOnRole, NotAuthorizedModuleBasedOnRole, NotAuthorizedRouteBasedOnSlug, OfficeRoles, Role } from '@shared/enums/roles.enum';
import { Observable, of, switchMap } from 'rxjs';
import { sanitizeReturnUrl } from '@core/auth/return-url.util';

@Injectable({
    providedIn: 'root'
})
export class AuthGuard 
{
    protected _authService = inject(AuthService);
    protected officeTenantsService = inject(OfficeTenantsService);
    protected _router = inject(Router);

    public isAppInit = false;

    public canMatch(route: Route, segments: UrlSegment[]): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree
    {
        return this.check(segments);
    }

    /** Current path plus the query string of the navigation in flight (canMatch only receives segments). */
    private getReturnUrl(currentPath: string): string {
        const queryParams = this._router.currentNavigation()?.extractedUrl.queryParams ?? {};
        const url = this._router.serializeUrl(this._router.createUrlTree([currentPath], { queryParams }));
        return sanitizeReturnUrl(url) ?? currentPath;
    }

    protected check(segments: UrlSegment[], roles: string[] = null): Observable<boolean | UrlTree>
    {
        const currentPath = `/${segments.join('/')}`;
        const guestPaths = guestsRoute?.map(route => route.path);
        // Check the authentication status
        return this._authService.check().pipe(
            switchMap((authenticated) => {
                const websites =  this.officeTenantsService.getAllTenantsViewItems();
                const websiteSlug = websites?.find(website => {
                    const split = currentPath.split('/');
                    return split.some(item => item.toLowerCase() === website.slug.toLowerCase());
                })?.slug;
                // If the user is not authenticated...
                if ( !authenticated )
                {
                    let url: string;
                    if (guestPaths.some(path => currentPath.includes(path))) {
                        return of(true);
                    }
                    // Check if the URL contains sign-in or sign-up and handle accordingly
                    if (currentPath.includes('/sign-in') || currentPath.includes('/sign-up')) {
                        // Split URL by 'sign-in' or 'sign-up' and dynamically adjust the redirect URL
                        const [baseUrl, query] = currentPath.split(/sign-in|sign-up/);
                        url = `${baseUrl}sign-in?redirectUrl=${query}`;
                    } else {
                        // Redirect to tenant sign-in page
                        if (websiteSlug) {
                            const query = currentPath.split(websiteSlug)[1];
                            url = query ? `${websiteSlug}/sign-in?redirectUrl=${query}` : `${websiteSlug}/sign-in`;
                        } 
                        // Default redirect to sign-in page
                        else {
                            url = `sign-in?redirectUrl=${encodeURIComponent(this.getReturnUrl(currentPath))}`;
                        }
                    }
                    const urlTree = this._router.parseUrl(url);
                    return of(urlTree);
                }

                const userRoles = this._authService.userProfile.userRoles;
                const hasCustomerRole = userRoles.some(ur => ur.roleName === Role.Customer);
                const hasAdminRole = userRoles.some(ur => ur.roleName === Role.Administrator);
                const hasWarehouseRole = userRoles.some(ur => ur.roleName === Role.Warehouse);
                const isWebsiteMatched = websiteSlug && this._authService.userProfile.userWebsites.some(website => website.slug === websiteSlug);
                const defaultRedirect = hasAdminRole ? 'office/reports' : 'dashboard';

                // Check if the current path matches any of the guest paths
                const isGuestRoute  = guestPaths?.filter(route => route !== 'public' && route !== 'rating').find((path) => {
                    const split = currentPath.split('/');
                    return split.some(item => item.toLowerCase() === path.toLowerCase());
                })
                if (isGuestRoute) {
                    return of(this._router.parseUrl(websiteSlug ? `${websiteSlug}/${defaultRedirect}` : `${defaultRedirect}`));
                }

                if (roles?.length) {
                    const hasRequiredRole = userRoles.some(ur => roles.find(r=>r === ur.roleName));
                    
                    // Check if the user has any restricted module based on their role
                    const isNotAuthorizedModule = userRoles.some(userRole => (NotAuthorizedModuleBasedOnRole as Partial<Record<string, string[]>>)[userRole.roleName]?.some(path => currentPath.includes(path)))
                        && !Object.values(Permission).some(permission => AuthorizedRouteBasedOnPermission[permission]?.some(path => currentPath.includes(path)) && this._authService.hasPermission(permission));

                    // Check if the user has any exact restricted route based on their role
                    const isNotAuthorizedExactRoute = userRoles.some(userRole => (NotAuthorizedExactRouteBasedOnRole as Partial<Record<string, string[]>>)[userRole.roleName]?.some(path => currentPath === path));
                    
                    // Check if the user has access to warehouse routes based on their role
                    const isWarehouseAuthorizedRoute = userRoles.some(userRole => (AuthorizedRouteBasedOnRole as Partial<Record<string, string[]>>)[userRole.roleName]?.some(path => currentPath.endsWith(path)));
                    
                    // Check if the current route is a restricted warehouse route
                    const isWarehouseNotAuthorizedRoute = hasWarehouseRole && currentPath.includes('/office/tenants') && !isWarehouseAuthorizedRoute;

                    if (!hasRequiredRole || isNotAuthorizedModule || isNotAuthorizedExactRoute || isWarehouseNotAuthorizedRoute) {
                        const urlNotAuthorized = this._router.parseUrl(websiteSlug ? `${websiteSlug}/not-authorized`: `not-authorized`);
                        return of(urlNotAuthorized);
                    }
                }

                // Check if the current path matches any of the restricted routes based on slug
                if (websiteSlug && !hasWarehouseRole && NotAuthorizedRouteBasedOnSlug.some(path => currentPath.includes(path))) {
                    return of(this._router.parseUrl(`${websiteSlug}/not-authorized`));
                }
                
                // Redirect to default path if website is matched.
                if (isWebsiteMatched && currentPath.includes('/confirm-membership')) {
                    return of(this._router.parseUrl(websiteSlug ? `${websiteSlug}/${defaultRedirect}` : `${defaultRedirect}`));
                } 
                
                // Redirect to 'confirm-membership' for customers not yet confirmed website.
                else if(websiteSlug && currentPath.includes(websiteSlug) && hasCustomerRole && !isWebsiteMatched && !currentPath.includes('confirm-membership')) {
                    return of(this._router.parseUrl(`${websiteSlug}/confirm-membership`));
                } 
                
                // Redirect to 'not-authorized' for non-admin.
                else if (websiteSlug && !currentPath.includes(websiteSlug) && !hasAdminRole && !hasCustomerRole && !currentPath.includes('not-authorized')) {
                    return of(this._router.parseUrl(`${websiteSlug}/not-authorized`));
                } 
                
                // Redirect to default path if there is no website slug.
                else if (currentPath === '/confirm-membership' && !currentPath.includes(defaultRedirect)) {
                    return of(this._router.parseUrl(websiteSlug ? `${websiteSlug}/${defaultRedirect}` : `${defaultRedirect}`));
                }

                // Allow the access
                return of(true);
            })
        );
    }
}

@Injectable({
    providedIn: 'root'
})
export class DriverAuthGuard extends AuthGuard
{
    authRoles = [Role.Driver];
    protected override check(segments: UrlSegment[]): Observable<boolean | UrlTree>
    {
        return super.check(segments, this.authRoles);
    }
}

@Injectable({
    providedIn: 'root'
})
export class OfficeAuthGuard extends AuthGuard
{
    protected override check(segments: UrlSegment[]): Observable<boolean | UrlTree>
    {
        return super.check(segments, OfficeRoles);
    }
}